import React, { useState, useEffect, useMemo } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Button,
  Input,
  Label,
  Badge,
  Table,
  UncontrolledTooltip,
  FormGroup,
} from "reactstrap";

const DEFAULT_OPTION_SUGGESTIONS = ["Size", "Color", "Material", "Style"];

// Helper: Cartesian product of option values
const generateCombinations = (optionsList) => {
  const validOptions = optionsList.filter(
    (opt) => opt.name && opt.name.trim() !== "" && Array.isArray(opt.values) && opt.values.length > 0
  );

  if (validOptions.length === 0) return [];

  const cartesian = (arrays) => {
    return arrays.reduce(
      (acc, curr) => {
        return acc.flatMap((a) => curr.map((c) => [...a, c]));
      },
      [[]]
    );
  };

  const optionValuesArrays = validOptions.map((opt) =>
    opt.values.map((val) => ({ optionName: opt.name.trim(), value: val.trim() }))
  );

  const combinations = cartesian(optionValuesArrays);

  return combinations.map((combo, idx) => {
    const title = combo.map((c) => c.value).join(" / ");
    const optionsObj = {};
    combo.forEach((c) => {
      optionsObj[c.optionName] = c.value;
    });

    return {
      comboKey: combo.map((c) => `${c.optionName}:${c.value}`).join("|"),
      title,
      options: optionsObj,
    };
  });
};

const ProductVariantManager = ({
  hasVariants = false,
  onHasVariantsChange,
  options = [],
  onOptionsChange,
  variants = [],
  onVariantsChange,
  basePrice = "",
  baseSku = "",
  baseStock = 0,
  currencySymbol = "£",
}) => {
  const [newOptionInput, setNewOptionInput] = useState("");
  const [newValueInputs, setNewValueInputs] = useState({});
  const [bulkPrice, setBulkPrice] = useState("");
  const [bulkStock, setBulkStock] = useState("");

  // Ensure options have clean structure: [{ id, name, values: [] }]
  const normalizedOptions = useMemo(() => {
    if (!Array.isArray(options)) return [];
    return options.map((opt, i) => ({
      id: opt.id || `opt_${i + 1}`,
      name: opt.name || "",
      values: Array.isArray(opt.values)
        ? opt.values
        : typeof opt.values === "string"
        ? opt.values.split(",").map((v) => v.trim()).filter(Boolean)
        : [],
    }));
  }, [options]);

  // Handle Add Option
  const handleAddOption = (optionName) => {
    const name = (optionName || newOptionInput).trim();
    if (!name) return;

    if (normalizedOptions.some((o) => o.name.toLowerCase() === name.toLowerCase())) {
      return;
    }

    const updated = [
      ...normalizedOptions,
      {
        id: `opt_${Date.now()}`,
        name,
        values: [],
      },
    ];
    onOptionsChange(updated);
    setNewOptionInput("");
  };

  // Handle Remove Option
  const handleRemoveOption = (optionId) => {
    const updated = normalizedOptions.filter((o) => o.id !== optionId);
    onOptionsChange(updated);
    // Regenerate variants after removing option
    syncVariantsWithCombinations(updated, variants);
  };

  // Handle Add Value Tag to Option
  const handleAddValue = (optionId) => {
    const val = (newValueInputs[optionId] || "").trim();
    if (!val) return;

    const updated = normalizedOptions.map((opt) => {
      if (opt.id === optionId) {
        const existing = opt.values || [];
        if (existing.includes(val)) return opt;
        return {
          ...opt,
          values: [...existing, val],
        };
      }
      return opt;
    });

    onOptionsChange(updated);
    setNewValueInputs((prev) => ({ ...prev, [optionId]: "" }));
    syncVariantsWithCombinations(updated, variants);
  };

  // Handle Remove Value Tag from Option
  const handleRemoveValue = (optionId, valueToRemove) => {
    const updated = normalizedOptions.map((opt) => {
      if (opt.id === optionId) {
        return {
          ...opt,
          values: opt.values.filter((v) => v !== valueToRemove),
        };
      }
      return opt;
    });

    onOptionsChange(updated);
    syncVariantsWithCombinations(updated, variants);
  };

  // Synchronize variants array with option combinations
  const syncVariantsWithCombinations = (currentOptions, existingVariants = []) => {
    const combinations = generateCombinations(currentOptions);
    if (combinations.length === 0) {
      onVariantsChange([]);
      return;
    }

    const existingMap = new Map();
    existingVariants.forEach((v) => {
      if (v.title) existingMap.set(v.title.toLowerCase(), v);
      if (v.comboKey) existingMap.set(v.comboKey, v);
    });

    const newVariants = combinations.map((combo, idx) => {
      const existing = existingMap.get(combo.comboKey) || existingMap.get(combo.title.toLowerCase());

      const cleanSkuPrefix = baseSku ? baseSku.trim() : "SKU";
      const cleanSkuSuffix = Object.values(combo.options)
        .map((v) => v.replace(/[^a-zA-Z0-9]/g, "").toUpperCase())
        .join("-");
      const defaultSku = `${cleanSkuPrefix}-${cleanSkuSuffix || idx + 1}`;

      if (existing) {
        return {
          ...existing,
          title: combo.title,
          options: combo.options,
          comboKey: combo.comboKey,
        };
      }

      return {
        id: `var_${Date.now()}_${idx}`,
        title: combo.title,
        sku: defaultSku,
        price: basePrice !== "" && basePrice !== null && basePrice !== undefined ? String(basePrice) : "0.00",
        stock_quantity: baseStock !== "" && baseStock !== null && baseStock !== undefined ? Number(baseStock) : 0,
        status: "active",
        options: combo.options,
        comboKey: combo.comboKey,
      };
    });

    onVariantsChange(newVariants);
  };

  // Auto-generate variants button
  const handleGenerateVariants = () => {
    syncVariantsWithCombinations(normalizedOptions, variants);
  };

  // Bulk update price
  const handleApplyBulkPrice = () => {
    if (bulkPrice === "" || isNaN(Number(bulkPrice))) return;
    const updated = variants.map((v) => ({
      ...v,
      price: String(bulkPrice),
    }));
    onVariantsChange(updated);
  };

  // Bulk update stock
  const handleApplyBulkStock = () => {
    if (bulkStock === "" || isNaN(Number(bulkStock))) return;
    const updated = variants.map((v) => ({
      ...v,
      stock_quantity: Number(bulkStock),
    }));
    onVariantsChange(updated);
  };

  // Bulk set all status
  const handleToggleAllStatus = (newStatus) => {
    const updated = variants.map((v) => ({
      ...v,
      status: newStatus,
    }));
    onVariantsChange(updated);
  };

  // Update single variant property
  const handleVariantChange = (index, field, value) => {
    const updated = [...variants];
    updated[index] = {
      ...updated[index],
      [field]: value,
    };
    onVariantsChange(updated);
  };

  // Remove single variant
  const handleRemoveVariant = (index) => {
    const updated = variants.filter((_, i) => i !== index);
    onVariantsChange(updated);
  };

  // Compute stats
  const activeVariantsCount = variants.filter((v) => v.status !== "disabled").length;
  const totalStockSum = variants.reduce((sum, v) => sum + (Number(v.stock_quantity) || 0), 0);
  const minPrice = useMemo(() => {
    const active = variants.filter((v) => v.status !== "disabled" && v.price !== "" && !isNaN(Number(v.price)));
    if (active.length === 0) return null;
    return Math.min(...active.map((v) => Number(v.price)));
  }, [variants]);
  const maxPrice = useMemo(() => {
    const active = variants.filter((v) => v.status !== "disabled" && v.price !== "" && !isNaN(Number(v.price)));
    if (active.length === 0) return null;
    return Math.max(...active.map((v) => Number(v.price)));
  }, [variants]);

  return (
    <div className="product-variant-manager mb-4">
      {/* Toggle Variable Product */}
      <div className="d-flex align-items-center justify-content-between p-3 bg-light rounded-3 border mb-3">
        <div>
          <h5 className="fs-14 mb-1 fw-semibold text-primary">
            <i className="ri-list-settings-line me-2 fs-16 align-middle"></i>
            Variable Product & Options
          </h5>
          <p className="text-muted fs-12 mb-0">
            Enable this if the product comes in multiple variations, such as different sizes, colors, or materials.
          </p>
        </div>
        <div className="form-check form-switch form-switch-md">
          <Input
            type="checkbox"
            className="form-check-input"
            id="hasVariantsSwitch"
            checked={Boolean(hasVariants)}
            onChange={(e) => onHasVariantsChange(e.target.checked)}
          />
          <Label className="form-check-label ms-1 fw-medium" htmlFor="hasVariantsSwitch">
            {hasVariants ? "Enabled" : "Disabled"}
          </Label>
        </div>
      </div>

      {hasVariants && (
        <Card className="border shadow-none mb-3">
          <CardHeader className="bg-soft-primary border-bottom py-2">
            <div className="d-flex align-items-center justify-content-between">
              <span className="fw-semibold text-primary fs-13">
                <i className="ri-equalizer-line me-1 align-middle"></i> 1. Configure Product Options
              </span>
              <div className="d-flex align-items-center gap-1">
                <span className="text-muted fs-12 me-1">Quick add:</span>
                {DEFAULT_OPTION_SUGGESTIONS.map((sug) => {
                  const alreadyAdded = normalizedOptions.some(
                    (o) => o.name.toLowerCase() === sug.toLowerCase()
                  );
                  if (alreadyAdded) return null;
                  return (
                    <Button
                      key={sug}
                      type="button"
                      color="light"
                      size="sm"
                      className="py-0 px-2 fs-11"
                      onClick={() => handleAddOption(sug)}
                    >
                      + {sug}
                    </Button>
                  );
                })}
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {/* Options List */}
            {normalizedOptions.length > 0 ? (
              <div className="d-flex flex-column gap-3 mb-3">
                {normalizedOptions.map((opt) => (
                  <div key={opt.id} className="p-3 border rounded bg-white">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <Badge color="primary" className="fs-12 px-2 py-1">
                          Option: {opt.name}
                        </Badge>
                        <span className="text-muted fs-12">
                          ({opt.values.length} {opt.values.length === 1 ? "value" : "values"})
                        </span>
                      </div>
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        outline
                        className="btn-icon py-0 px-2"
                        title="Remove Option"
                        onClick={() => handleRemoveOption(opt.id)}
                      >
                        <i className="ri-delete-bin-line"></i>
                      </Button>
                    </div>

                    {/* Value tags */}
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                      {opt.values.map((val) => (
                        <span
                          key={val}
                          className="badge bg-light text-dark border px-2 py-1 fs-12 d-inline-flex align-items-center gap-1"
                        >
                          {val}
                          <i
                            className="ri-close-line text-danger cursor-pointer ms-1"
                            style={{ cursor: "pointer", fontSize: "14px" }}
                            onClick={() => handleRemoveValue(opt.id, val)}
                            title="Remove value"
                          ></i>
                        </span>
                      ))}
                      {opt.values.length === 0 && (
                        <span className="text-muted fs-12 fst-italic">No values added yet. Add at least one below.</span>
                      )}
                    </div>

                    {/* Add value input */}
                    <div className="d-flex align-items-center gap-2 mt-2" style={{ maxWidth: "420px" }}>
                      <Input
                        type="text"
                        size="sm"
                        placeholder={`Type a value for ${opt.name} (e.g. Red, XL, Cotton) & press Enter`}
                        value={newValueInputs[opt.id] || ""}
                        onChange={(e) =>
                          setNewValueInputs({
                            ...newValueInputs,
                            [opt.id]: e.target.value,
                          })
                        }
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleAddValue(opt.id);
                          }
                        }}
                      />
                      <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        className="text-nowrap"
                        onClick={() => handleAddValue(opt.id)}
                      >
                        <i className="ri-add-line me-1"></i> Add
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-3 border border-dashed rounded bg-light mb-3">
                <i className="ri-tools-line fs-24 text-muted d-block mb-1"></i>
                <p className="text-muted fs-13 mb-2">
                  No options created yet. Add options like <strong>Size</strong>, <strong>Color</strong>, or <strong>Material</strong> to get started.
                </p>
              </div>
            )}

            {/* Add Custom Option Input */}
            <div className="d-flex align-items-center gap-2" style={{ maxWidth: "450px" }}>
              <Input
                type="text"
                size="sm"
                placeholder="New option name (e.g. Flavor, Capacity, Pattern)"
                value={newOptionInput}
                onChange={(e) => setNewOptionInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleAddOption();
                  }
                }}
              />
              <Button
                type="button"
                color="primary"
                size="sm"
                className="text-nowrap"
                onClick={() => handleAddOption()}
              >
                <i className="ri-add-circle-line me-1"></i> Add Option
              </Button>
            </div>
          </CardBody>
        </Card>
      )}

      {/* 2. Variants Matrix / Table */}
      {hasVariants && (
        <Card className="border shadow-none mb-3">
          <CardHeader className="bg-soft-success border-bottom py-2">
            <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <div className="d-flex align-items-center gap-2">
                <span className="fw-semibold text-success fs-13">
                  <i className="ri-table-line me-1 align-middle"></i> 2. Product Variants Matrix
                </span>
                {variants.length > 0 && (
                  <Badge color="success" pill className="fs-11">
                    {activeVariantsCount} Active / {variants.length} Total
                  </Badge>
                )}
              </div>

              <div className="d-flex flex-wrap align-items-center gap-2">
                <Button
                  type="button"
                  color="success"
                  size="sm"
                  outline
                  className="py-1 px-2 fs-12"
                  onClick={handleGenerateVariants}
                >
                  <i className="ri-refresh-line me-1 align-middle"></i> Re-generate Variants
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  className="py-1 px-2 fs-12"
                  onClick={() => handleToggleAllStatus("active")}
                >
                  Enable All
                </Button>
                <Button
                  type="button"
                  color="light"
                  size="sm"
                  className="py-1 px-2 fs-12"
                  onClick={() => handleToggleAllStatus("disabled")}
                >
                  Disable All
                </Button>
              </div>
            </div>
          </CardHeader>

          <CardBody>
            {/* Quick Bulk Update Bar */}
            {variants.length > 0 && (
              <div className="p-2 mb-3 bg-light rounded border d-flex flex-wrap align-items-center justify-content-between gap-2">
                <span className="text-muted fs-12 fw-medium">
                  <i className="ri-flashlight-line me-1 text-warning"></i> Bulk Actions:
                </span>
                <div className="d-flex flex-wrap align-items-center gap-2">
                  <div className="input-group input-group-sm" style={{ width: "160px" }}>
                    <span className="input-group-text">{currencySymbol}</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Bulk Price"
                      value={bulkPrice}
                      onChange={(e) => setBulkPrice(e.target.value)}
                    />
                    <Button color="secondary" type="button" onClick={handleApplyBulkPrice}>
                      Set
                    </Button>
                  </div>

                  <div className="input-group input-group-sm" style={{ width: "150px" }}>
                    <Input
                      type="number"
                      placeholder="Bulk Stock"
                      value={bulkStock}
                      onChange={(e) => setBulkStock(e.target.value)}
                    />
                    <Button color="secondary" type="button" onClick={handleApplyBulkStock}>
                      Set
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {/* Variants Table */}
            {variants.length > 0 ? (
              <div className="table-responsive">
                <Table className="align-middle table-nowrap mb-0 table-hover table-bordered">
                  <thead className="table-light">
                    <tr>
                      <th style={{ width: "50px" }}>#</th>
                      <th>Variant Combination</th>
                      <th style={{ width: "180px" }}>SKU</th>
                      <th style={{ width: "150px" }}>Price ({currencySymbol})</th>
                      <th style={{ width: "130px" }}>Stock Qty</th>
                      <th style={{ width: "100px" }}>Status</th>
                      <th style={{ width: "60px" }} className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.map((v, idx) => {
                      const isDisabled = v.status === "disabled";
                      return (
                        <tr key={v.id || idx} className={isDisabled ? "table-light opacity-50" : ""}>
                          <td className="text-muted">{idx + 1}</td>
                          <td>
                            <div className="d-flex align-items-center gap-1 flex-wrap">
                              <span className="fw-medium text-dark">{v.title}</span>
                              {v.options &&
                                Object.entries(v.options).map(([k, val]) => (
                                  <Badge key={k} color="soft-primary" className="text-primary fs-11 ms-1">
                                    {k}: {val}
                                  </Badge>
                                ))}
                            </div>
                          </td>
                          <td>
                            <Input
                              type="text"
                              size="sm"
                              className="form-control-sm"
                              value={v.sku || ""}
                              placeholder="SKU"
                              onChange={(e) => handleVariantChange(idx, "sku", e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="input-group input-group-sm">
                              <span className="input-group-text">{currencySymbol}</span>
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                className="form-control-sm"
                                value={v.price !== undefined ? v.price : ""}
                                placeholder="0.00"
                                onChange={(e) => handleVariantChange(idx, "price", e.target.value)}
                              />
                            </div>
                          </td>
                          <td>
                            <Input
                              type="number"
                              min="0"
                              size="sm"
                              className="form-control-sm"
                              value={v.stock_quantity !== undefined ? v.stock_quantity : 0}
                              placeholder="0"
                              onChange={(e) => handleVariantChange(idx, "stock_quantity", e.target.value)}
                            />
                          </td>
                          <td>
                            <div className="form-check form-switch form-switch-sm">
                              <Input
                                type="checkbox"
                                className="form-check-input"
                                checked={v.status !== "disabled"}
                                onChange={(e) =>
                                  handleVariantChange(idx, "status", e.target.checked ? "active" : "disabled")
                                }
                              />
                              <Label className="form-check-label fs-11 ms-1">
                                {v.status !== "disabled" ? "Active" : "Off"}
                              </Label>
                            </div>
                          </td>
                          <td className="text-center">
                            <Button
                              type="button"
                              color="link"
                              size="sm"
                              className="text-danger p-0"
                              title="Delete Variant"
                              onClick={() => handleRemoveVariant(idx)}
                            >
                              <i className="ri-delete-bin-5-line fs-15"></i>
                            </Button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            ) : (
              <div className="text-center py-4 border border-dashed rounded bg-light">
                <i className="ri-grid-line fs-28 text-muted d-block mb-1"></i>
                <p className="text-muted fs-13 mb-2">
                  No variants generated. Add options and values above, then click <strong>Re-generate Variants</strong>.
                </p>
              </div>
            )}

            {/* Summary Footer */}
            {variants.length > 0 && (
              <div className="d-flex flex-wrap align-items-center justify-content-between mt-3 pt-2 border-top text-muted fs-12">
                <div>
                  <strong>Total Stock Across All Variants:</strong>{" "}
                  <span className="text-dark fw-semibold">{totalStockSum} units</span>
                </div>
                <div>
                  <strong>Effective Price Range:</strong>{" "}
                  <span className="text-success fw-semibold">
                    {minPrice !== null && maxPrice !== null
                      ? minPrice === maxPrice
                        ? `${currencySymbol}${minPrice.toFixed(2)}`
                        : `${currencySymbol}${minPrice.toFixed(2)} - ${currencySymbol}${maxPrice.toFixed(2)}`
                      : "Not set"}
                  </span>
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ProductVariantManager;
